import * as THREE from 'three';
import ClipperLib from 'clipper-lib';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import sourceProfiles from './swing-profiles.json';
import { member } from './fixedWindows';

// HD PDF sheets 7–10: 30mm installation fin outside the visible opening,
// 46mm frame face + 63mm exposed leaf = 109mm sightline. Raw CAD remains intact.
export const SWING_CAD = { installationFin: .030, rebateOverlap: .0055, centerGap: .022, thresholdRise: .030, glazingBite: .015, glassZ: -.0055 };
export const SWING_SETS = {
  single: { frame: '315722', leaf: '315720', sill: '315727', highSill: '315730', mullion: '315731' },
  double: { frame: '315725', leaf: '315720', sill: '315729', highSill: '315683', mullion: '315731' },
};
const sections = new Map(), extrusions = new Map();

function visibleShape(shape, depth, height) {
  const S = 10000, fin = SWING_CAD.installationFin * 1000;
  const path = ring => ring.map(([x, y]) => ({ X: Math.round(x * S), Y: Math.round(y * S) }));
  const clipper = new ClipperLib.Clipper(), tree = new ClipperLib.PolyTree();
  clipper.AddPaths([shape.outer, ...(shape.holes || [])].map(path), ClipperLib.PolyType.ptSubject, true);
  clipper.AddPath(path([[-1, fin], [depth + 1, fin], [depth + 1, height + 1], [-1, height + 1]]), ClipperLib.PolyType.ptClip, true);
  clipper.Execute(ClipperLib.ClipType.ctIntersection, tree, ClipperLib.PolyFillType.pftEvenOdd, ClipperLib.PolyFillType.pftEvenOdd);
  const points = node => node.Contour().map(p => [p.X / S, p.Y / S - fin]);
  const result = [];
  const visit = node => { for (const child of node.Childs()) {
    if (!child.IsHole()) result.push({ outer: points(child), holes: child.Childs().filter(h => h.IsHole()).map(points) });
    visit(child);
  } };
  visit(tree); return result;
}

export function swingSection(code) {
  if (!sections.has(code)) {
    const raw = sourceProfiles[code], trim = Object.values(SWING_SETS).some(s => s.frame === code);
    sections.set(code, trim ? { ...raw, height_mm: raw.height_mm - SWING_CAD.installationFin * 1000,
      components: raw.components.map(c => ({ ...c, shapes: c.shapes.flatMap(s => visibleShape(s, raw.depth_mm, raw.height_mm)) })) } : raw);
  }
  return sections.get(code);
}

export function assembleSwing(parent, parts, width, height, roles) {
  for (const [code, sides] of parts) {
    const data = swingSection(code);
    if (!extrusions.has(code)) extrusions.set(code, data.components.filter(c => c.shapes.length).map(c => {
      const shape = s => { const outer = new THREE.Shape(s.outer.map(([x,y]) => new THREE.Vector2(x / 1000, y / 1000))); s.holes.forEach(h => outer.holes.push(new THREE.Path(h.map(([x,y]) => new THREE.Vector2(x / 1000, y / 1000))))); return outer; };
      return { role: c.role, geometry: new THREE.ExtrudeGeometry(c.shapes.map(shape), { depth: 1, steps: 1, bevelEnabled: false, curveSegments: 3 }) };
    }));
    for (const { role, geometry } of extrusions.get(code)) {
      const pieces = sides.map(side => member(geometry, side, width, height).translate(0, 0, -data.depth_mm / 2000));
      const mesh = new THREE.Mesh(mergeGeometries(pieces, false), roles[role] || roles.frame);
      pieces.forEach(g => g.dispose()); mesh.castShadow = true; mesh.receiveShadow = true;
      Object.assign(mesh.userData, { profileCode: code, profileRole: role, profileDepthMm: data.depth_mm }); parent.add(mesh);
    }
  }
}

export function swingDimensions(options) {
  const parts = SWING_SETS[options.leaves === 'double' ? 'double' : 'single'];
  const frame = swingSection(parts.frame), leaf = swingSection(parts.leaf), rise = options.threshold === 'high' ? SWING_CAD.thresholdRise : 0;
  const sill = rise + swingSection(parts.sill).height_mm / 1000;
  return { parts, face: frame.height_mm / 1000, leafFace: leaf.height_mm / 1000, sill, rise,
    leafInset: frame.height_mm / 1000 - SWING_CAD.rebateOverlap, bottom: sill - SWING_CAD.rebateOverlap,
    leafZ: (frame.depth_mm - leaf.depth_mm) / 2000, depth: frame.depth_mm / 1000 };
}

export function addSwingThreshold(group, dimensions, width, height, roles) {
  const { parts, rise } = dimensions;
  if (rise) assembleSwing(group, [[parts.highSill, ['sill']]], width, height, roles);
  const upper = new THREE.Group(); upper.position.y = rise;
  assembleSwing(upper, [[parts.sill, ['sill']]], width, height, roles); group.add(upper);
}